import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { hourList, minuteList } from '@utils/number-arrays';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { DescriptionFormField } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { FormGroupContainer } from './journey-sidebar';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  EditableActionContent,
  FormSidebarFooter,
  getWaitForTimeLength,
  isActionError,
  SidebarProps,
  TravellerActionActions,
  TravellerActionHeader,
  useEditingContext,
} from '../common';
import { ActionTimeWithTimezoneTooltip, newDateIgnoreLocalTimezone } from '../timezone';

const ACTION_TYPE = ACTION_TYPES.wait_for_time;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE}>
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();
  const data = action.data;
  const scheduledDate = action?.scheduledDate;
  const selectedDate = scheduledDate ? newDateIgnoreLocalTimezone(scheduledDate as string) : null;

  const hours = selectedDate?.getHours().toString();
  const minutes = selectedDate?.getMinutes().toString();
  const [offset] = action?.timezone?.split(' ') ?? [''];

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  return (
    <Stack space={6}>
      <ActionProgressBar
        action={action}
        progressText={progressText}
        isError={isError}
        isCard={false}
        progressPercentage=" "
      />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <div>
            <Text font="semibold14" text={t('configuration')} />
            <Text font="regular14" text={t('configurationDescription')} />
          </div>
          <div>
            {data.days !== '0' && (
              <Text
                font="semibold14"
                text={data.days === '1' ? t('dayConfig') : t('daysConfig', { total: data.days })}
              />
            )}
            {data.hours !== '0' && (
              <Text
                font="semibold14"
                text={
                  data.hours === '1' ? t('hourConfig') : t('hoursConfig', { total: data.hours })
                }
              />
            )}
            {data.minutes !== '0' && (
              <Text
                font="semibold14"
                text={
                  data.minutes === '1'
                    ? t('minuteConfig')
                    : t('minutesConfig', { total: data.minutes })
                }
              />
            )}
          </div>
          <Text font="regular14" text={t('beforeContinuing')} />
          {selectedDate && (
            <div>
              <Text font="semibold14" text={t('triggersNextAction')} />
              <ActionTimeWithTimezoneTooltip
                actionText={abbreviateDate(selectedDate, 'en', 'datetime')}
                hour={hours as string}
                minute={minutes as string}
                journeyTimezoneOffset={offset}
              />
            </div>
          )}
        </Stack>
      </HideContainer>
      {status !== 'completed' && (
        <Sidebar.Footer>
          <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

type WaitForData = {
  days: string;
  hours: string;
  minutes: string;
  description: string;
};

const Form = createForm<WaitForData>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const days = (formStore.useFieldValue('days') ?? '') as string;
  const hours = (formStore.useFieldValue('hours') ?? '') as string;
  const minutes = (formStore.useFieldValue('minutes') ?? '') as string;

  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: WaitForData) => {
    return editMutation.mutateAsync({
      values: {
        days: { value: data.days },
        hours: { value: data.hours },
        minutes: { value: data.minutes },
      },
      description: data.description,
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <Stack space={4}>
          <Text text={t('selectHowLong')} font="regular14" />
          <Text text={t('useWhen')} font="regular14" />
        </Stack>
        <Separator />

        <FormGroupContainer>
          <Form.TextField
            id="days"
            label={t('days')}
            defaultValue={action.values?.days?.value ?? '0'}
            validators={['number']}
          />
          <Form.Select
            id="hours"
            label={t('hours')}
            options={hourList}
            placeholder=""
            defaultValue={action.values?.hours?.value ?? '0'}
            isClearable={false}
          />
          <Form.Select
            id="minutes"
            label={t('minutes')}
            options={minuteList}
            placeholder=""
            defaultValue={action.values?.minutes?.value ?? '0'}
            isClearable={false}
          />
        </FormGroupContainer>
        <Text
          text={t('waitsForDescription', {
            timeLengthText: getWaitForTimeLength(days, hours, minutes),
          })}
        />
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    actionDetails: 'Action details',
    description: 'Description',
    configuration: 'Configuration',
    configurationDescription: 'The wait for action is configured to wait for',
    dayConfig: '1 • Day',
    daysConfig: '{total} • Days',
    hourConfig: '1 • Hour',
    hoursConfig: '{total} • Hours',
    minuteConfig: '1 • Minute',
    minutesConfig: '{total} • Minutes',
    beforeContinuing: 'Before triggering the following action',
    selectHowLong:
      'Select for how long journey step should wait before triggreing the following action',
    useWhen: 'Use when you want to pause by a defined amount of time between actions in a journey',
    waitsForDescription: 'Waits for {timeLengthText} before triggering the following action',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    daysPassed: '{days} Days',
    triggersNextAction: 'Triggers next action',
  },
};
